import React from 'react';
import { useCRM } from '../context/CRMContext';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { formatCurrency } from '../utils/calculations';
import { TrendingUpIcon, UsersIcon, UserCheckIcon, UserXIcon, DollarSignIcon } from 'lucide-react';
export function Funnel() {
  const {
    selectedCompany
  } = useData();
  const {
    getContactsByCompanyId
  } = useCRM();
  const contacts = selectedCompany ? getContactsByCompanyId(selectedCompany.id) : [];
  // Calculate funnel metrics
  const leads = contacts.filter(c => c.status === 'lead');
  const contactStatus = contacts.filter(c => c.status === 'contact');
  const customers = contacts.filter(c => c.status === 'customer');
  const lost = contacts.filter(c => c.status === 'lost');
  const totalRevenue = customers.reduce((sum, customer) => {
    return sum + customer.companies.reduce((companySum, rel) => companySum + rel.lifetime_purchase, 0);
  }, 0);
  const avgDealSize = customers.length > 0 ? totalRevenue / customers.length : 0;
  // Conversion rates
  const leadToContactRate = leads.length > 0 ? contactStatus.length / (leads.length + contactStatus.length) * 100 : 0;
  const contactToCustomerRate = contactStatus.length > 0 ? customers.length / (contactStatus.length + customers.length) * 100 : 0;
  const overallConversionRate = leads.length + contactStatus.length > 0 ? customers.length / (leads.length + contactStatus.length + customers.length) * 100 : 0;
  const funnelStages = [{
    name: 'Leads',
    count: leads.length,
    percentage: 100,
    color: 'bg-yellow-500',
    icon: UsersIcon
  }, {
    name: 'Contacts',
    count: contactStatus.length,
    percentage: leads.length > 0 ? contactStatus.length / leads.length * 100 : 0,
    color: 'bg-blue-500',
    icon: TrendingUpIcon
  }, {
    name: 'Customers',
    count: customers.length,
    percentage: contactStatus.length > 0 ? customers.length / contactStatus.length * 100 : 0,
    color: 'bg-green-500',
    icon: UserCheckIcon
  }];
  return <div className="space-y-6" data-id="element-1207">
      <div data-id="element-1208">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2" data-id="element-1209">
          Sales Funnel
        </h1>
        <p className="text-gray-600" data-id="element-1210">
          Track your pipeline and conversion rates
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1211">
        <Card data-id="element-1212">
          <div className="flex items-center justify-between" data-id="element-1213">
            <div data-id="element-1214">
              <p className="text-sm text-gray-600 mb-1" data-id="element-1215">Total Contacts</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1216">
                {contacts.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center" data-id="element-1217">
              <UsersIcon className="w-6 h-6 text-gray-600" data-id="element-1218" />
            </div>
          </div>
        </Card>

        <Card data-id="element-1219">
          <div className="flex items-center justify-between" data-id="element-1220">
            <div data-id="element-1221">
              <p className="text-sm text-gray-600 mb-1" data-id="element-1222">Customers</p>
              <p className="text-2xl font-bold text-green-600" data-id="element-1223">
                {customers.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-1224">
              <UserCheckIcon className="w-6 h-6 text-green-600" data-id="element-1225" />
            </div>
          </div>
        </Card>

        <Card data-id="element-1226">
          <div className="flex items-center justify-between" data-id="element-1227">
            <div data-id="element-1228">
              <p className="text-sm text-gray-600 mb-1" data-id="element-1229">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1230">
                {formatCurrency(totalRevenue)}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-1231">
              <DollarSignIcon className="w-6 h-6 text-blue-600" data-id="element-1232" />
            </div>
          </div>
        </Card>

        <Card data-id="element-1233">
          <div className="flex items-center justify-between" data-id="element-1234">
            <div data-id="element-1235">
              <p className="text-sm text-gray-600 mb-1" data-id="element-1236">Avg Deal Size</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1237">
                {formatCurrency(avgDealSize)}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-1238">
              <TrendingUpIcon className="w-6 h-6 text-purple-600" data-id="element-1239" />
            </div>
          </div>
        </Card>
      </div>

      {/* Funnel Visualization */}
      <Card data-id="element-1240">
        <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-1241">
          Pipeline Stages
        </h2>
        <div className="space-y-6" data-id="element-1242">
          {funnelStages.map((stage, index) => {
          const Icon = stage.icon;
          return <div key={stage.name} data-id="element-1243">
                <div className="flex items-center justify-between mb-2" data-id="element-1244">
                  <div className="flex items-center gap-3" data-id="element-1245">
                    <div className={`w-10 h-10 ${stage.color.replace('bg-', 'bg-').replace('-500', '-100')} rounded-lg flex items-center justify-center`} data-id="element-1246">
                      <Icon className={`w-5 h-5 ${stage.color.replace('bg-', 'text-')}`} data-id="element-1247" />
                    </div>
                    <div data-id="element-1248">
                      <h3 className="text-sm font-semibold text-gray-900" data-id="element-1249">
                        {stage.name}
                      </h3>
                      <p className="text-xs text-gray-500" data-id="element-1250">
                        {stage.count} contacts
                        {index > 0 && ` • ${stage.percentage.toFixed(1)}% conversion`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-1251">
                    <p className="text-lg font-bold text-gray-900" data-id="element-1252">
                      {stage.count}
                    </p>
                  </div>
                </div>
                <div className="relative h-12 bg-gray-100 rounded-lg overflow-hidden" data-id="element-1253">
                  <div className={`absolute inset-y-0 left-0 ${stage.color} transition-all duration-500 flex items-center justify-center`} style={{
                width: `${Math.max(stage.percentage, 5)}%`
              }} data-id="element-1254">
                    <span className="text-white text-sm font-semibold" data-id="element-1255">
                      {stage.percentage.toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </Card>

      {/* Conversion Rates & Lost */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1256">
        <Card data-id="element-1257">
          <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1258">
            Conversion Rates
          </h2>
          <div className="space-y-4" data-id="element-1259">
            <div data-id="element-1260">
              <div className="flex items-center justify-between mb-2" data-id="element-1261">
                <span className="text-sm text-gray-600" data-id="element-1262">Lead → Contact</span>
                <span className="text-sm font-semibold text-gray-900" data-id="element-1263">
                  {leadToContactRate.toFixed(1)}%
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1264">
                <div className="h-full bg-blue-500 transition-all duration-500" style={{
                width: `${leadToContactRate}%`
              }} data-id="element-1265" />
              </div>
            </div>
            <div data-id="element-1266">
              <div className="flex items-center justify-between mb-2" data-id="element-1267">
                <span className="text-sm text-gray-600" data-id="element-1268">
                  Contact → Customer
                </span>
                <span className="text-sm font-semibold text-gray-900" data-id="element-1269">
                  {contactToCustomerRate.toFixed(1)}%
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1270">
                <div className="h-full bg-green-500 transition-all duration-500" style={{
                width: `${contactToCustomerRate}%`
              }} data-id="element-1271" />
              </div>
            </div>
            <div data-id="element-1272">
              <div className="flex items-center justify-between mb-2" data-id="element-1273">
                <span className="text-sm text-gray-600" data-id="element-1274">
                  Overall Conversion
                </span>
                <span className="text-sm font-semibold text-gray-900" data-id="element-1275">
                  {overallConversionRate.toFixed(1)}%
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1276">
                <div className="h-full bg-purple-500 transition-all duration-500" style={{
                width: `${overallConversionRate}%`
              }} data-id="element-1277" />
              </div>
            </div>
          </div>
        </Card>

        <Card data-id="element-1278">
          <div className="flex items-center justify-between mb-4" data-id="element-1279">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-1280">
              Lost Opportunities
            </h2>
            <div className="flex items-center gap-2" data-id="element-1281">
              <UserXIcon className="w-5 h-5 text-gray-400" data-id="element-1282" />
              <span className="text-2xl font-bold text-gray-900" data-id="element-1283">
                {lost.length}
              </span>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-4" data-id="element-1284">
            {lost.length} contacts marked as lost
          </p>
          {lost.length > 0 && <div className="space-y-2" data-id="element-1285">
              <p className="text-xs font-semibold text-gray-500 uppercase" data-id="element-1286">
                Recent Lost Contacts
              </p>
              {lost.slice(0, 5).map(contact => <div key={contact.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0" data-id="element-1287">
                  <div data-id="element-1288">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1289">
                      {contact.first_name} {contact.last_name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1290">{contact.company}</p>
                  </div>
                </div>)}
            </div>}
        </Card>
      </div>
    </div>;
}